import React, { useMemo, useState, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { Modal, Button, Spinner, Tab, Nav } from 'react-bootstrap'
import {
  FaCheckCircle,
  FaRobot,
  FaUser,
  FaChartBar,
  FaLightbulb,
  FaBullseye,
  FaBrain,
  FaHandPointer,
  FaEye,
  FaImages,
} from 'react-icons/fa'
import { Grid3x3, Grid2x2, LayoutGrid } from 'lucide-react'
import { useGetContentImageQuery } from '../../../../services/contentApi'
import { useGetTagsQuery } from '../../../../services/tagApi'
import { useGetProjectQuery } from '../../../../services/projectsApi'
import { useAppSelector } from '../../../../store/hooks'
import { selectSelectedProjectId } from '../../../../store/slices/projectSlice'
import GalleryGrid from '../../../gallery/Grid'
import GalleryTile from '../../../gallery/Tile'
import ContentImage from '../../../tools/ContentImage'

const subtlePulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(147, 51, 234, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(147, 51, 234, 0);
  }
`

const ProgressCard = styled.div`
  background: linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid rgba(147, 51, 234, 0.2);
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  &:hover {
    border-color: rgba(147, 51, 234, 0.4);
  }
`

const CardHeader = styled.div`
  text-align: center;
  padding: 0.75rem;
  background: rgba(147, 51, 234, 0.08);
  border-radius: 8px;
  border: 1px solid rgba(147, 51, 234, 0.2);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  animation: ${subtlePulse} 3s ease-in-out infinite;

  &:hover {
    background: rgba(147, 51, 234, 0.15);
    border-color: rgba(147, 51, 234, 0.4);
    transform: translateY(-1px);
    animation: none;
  }
`

const CardTitle = styled.h3`
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const Section = styled.div`
  flex-shrink: 0;
`

const SectionTitle = styled.h4`
  color: #c084fc;
  font-size: 0.75rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  text-transform: uppercase;
  letter-spacing: 0.8px;

  svg {
    color: #9333ea;
  }
`

const ProgressBarContainer = styled.div`
  margin-bottom: 0.5rem;
`

const ProgressBar = styled.div`
  background: rgba(64, 64, 64, 0.5);
  border-radius: 8px;
  height: 8px;
  overflow: hidden;
  margin-bottom: 0.5rem;
  position: relative;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.3);
`

const ProgressFill = styled.div<{ $percentage: number; $color?: string }>`
  background: ${(props) => props.$color || 'linear-gradient(90deg, #9333ea 0%, #c084fc 100%)'};
  height: 100%;
  width: ${(props) => props.$percentage}%;
  transition: width 0.5s ease;
  border-radius: 8px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.2) 50%,
      transparent 100%
    );
    animation: shimmer 2s infinite;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`

const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.7rem;

  span:first-child {
    color: #9333ea;
    font-weight: 700;
  }

  span:last-child {
    color: #b0b0b0;
    font-weight: 500;
  }
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`

const StatItem = styled.div`
  background: linear-gradient(135deg, rgba(147, 51, 234, 0.05) 0%, rgba(147, 51, 234, 0.1) 100%);
  border-radius: 8px;
  padding: 0.6rem 0.4rem;
  text-align: center;
  border: 1px solid rgba(147, 51, 234, 0.15);
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(147, 51, 234, 0.3);
    background: linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(147, 51, 234, 0.15) 100%);
    transform: translateY(-1px);
  }
`

const StatValue = styled.div`
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 800;
  margin-bottom: 0.2rem;
  background: linear-gradient(135deg, #c084fc 0%, #9333ea 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`

const StatLabel = styled.div`
  color: #b0b0b0;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const InfoBox = styled.div<{ $type?: 'tip' | 'warning' | 'info' }>`
  background: ${(props) => {
    switch (props.$type) {
      case 'tip':
        return 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(16, 185, 129, 0.12) 100%)'
      case 'warning':
        return 'linear-gradient(135deg, rgba(255, 193, 7, 0.08) 0%, rgba(255, 193, 7, 0.12) 100%)'
      default:
        return 'linear-gradient(135deg, rgba(147, 51, 234, 0.08) 0%, rgba(147, 51, 234, 0.12) 100%)'
    }
  }};
  border: 1px solid
    ${(props) => {
      switch (props.$type) {
        case 'tip':
          return 'rgba(16, 185, 129, 0.3)'
        case 'warning':
          return 'rgba(255, 193, 7, 0.3)'
        default:
          return 'rgba(147, 51, 234, 0.3)'
      }
    }};
  border-radius: 8px;
  padding: 0.6rem;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`

const InfoText = styled.p`
  color: #e0e0e0;
  font-size: 0.68rem;
  margin: 0;
  line-height: 1.4;
  flex: 1;

  strong {
    color: #ffffff;
    font-weight: 600;
  }
`

const ConfidenceBreakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const ConfidenceItem = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  background: rgba(60, 60, 60, 0.4);
  border-radius: 6px;
  border-left: 4px solid ${(props) => props.$color};
  transition: all 0.2s ease;

  &:hover {
    background: rgba(60, 60, 60, 0.6);
    transform: translateX(2px);
  }
`

const ConfidenceName = styled.span`
  color: #e0e0e0;
  font-size: 0.7rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`

const ConfidenceCount = styled.span`
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 700;
  background: rgba(147, 51, 234, 0.2);
  padding: 0.15rem 0.5rem;
  border-radius: 12px;
  min-width: 32px;
  text-align: center;
`

const TimeDisplay = styled.div`
  text-align: center;
  color: #9333ea;
  font-size: 0.8rem;
  font-weight: 600;
`

interface ReviewedImageProps {
  contentId: string
  projectId: string
  datasetId: string
  width: number
  height: number
  bboxes: any[]
  annotation?: any[]
  onClick?: () => void
}

const ReviewedImageWithBBoxes: React.FC<ReviewedImageProps> = ({
  contentId,
  projectId,
  datasetId,
  width,
  height,
  bboxes,
  annotation,
  onClick,
}) => {
  // Construct a Content object for ContentImage
  const content = React.useMemo(() => {
    return {
      id: contentId,
      project_id: projectId,
      dataset_id: datasetId,
      width,
      height,
      annotation: annotation || [], // Use full annotation structure if provided
    }
  }, [contentId, projectId, datasetId, width, height, annotation])

  return (
    <ContentImage content={content as any} onImageClick={() => onClick?.()} selectable={false} />
  )
}

interface ReviewProgressCardProps {
  confidenceDistribution: {
    none: number
    high: number
    medium: number
    low: number
  } | null
  totalImages: number
  batchStartTime?: string
  batchStatus?: string
  originalUnlabeledCount: number
  completedCount: number
  reviewedImages?: Array<{
    contentid: string
    projectId: string
    datasetId: string
    width: number
    height: number
    bboxes: any[]
    annotation?: any[]
  }>
}

const ReviewProgressCard: React.FC<ReviewProgressCardProps> = ({
  confidenceDistribution,
  totalImages,
  batchStartTime,
  batchStatus,
  originalUnlabeledCount,
  completedCount,
  reviewedImages = [],
}) => {
  const [elapsedTime, setElapsedTime] = useState('')
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedImageDetail, setSelectedImageDetail] = useState<{
    contentId: string
    projectId: string
    datasetId: string
    width: number
    height: number
    bboxes: any[]
  } | null>(null)
  const [imagesPerRow, setImagesPerRow] = useState(3) // Default to 3 images per row

  // Fetch project and tags for mapping bbox names
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const { data: tags } = useGetTagsQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !selectedProjectId || !project?.datasetid }
  )

  // Fetch detailed image for modal
  const { data: detailedImageUrl, isLoading: isDetailedImageLoading } = useGetContentImageQuery(
    {
      ContentID: selectedImageDetail?.contentId || '',
      project_id: selectedImageDetail?.projectId || '',
      dataset_id: selectedImageDetail?.datasetId || '',
    },
    {
      skip: !selectedImageDetail?.contentId,
    }
  )

  // Helper function to get tag name from ID
  const getTagName = (tagId: string | undefined) => {
    if (!tagId || !tags?.tags) return tagId || 'Unknown'
    const tag = tags.tags.find((t) => t.id === tagId)
    return tag?.name || tagId
  }

  // Calculate elapsed time since batch started
  useEffect(() => {
    if (!batchStartTime) return

    const updateElapsedTime = () => {
      const start = new Date(batchStartTime)
      const now = new Date()
      const diffMs = now.getTime() - start.getTime()

      const hours = Math.floor(diffMs / (1000 * 60 * 60))
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

      if (hours > 0) {
        setElapsedTime(`${hours}h ${minutes}m`)
      } else {
        setElapsedTime(`${minutes}m`)
      }
    }

    updateElapsedTime()
    const interval = setInterval(updateElapsedTime, 60000) // Update every minute

    return () => clearInterval(interval)
  }, [batchStartTime])

  // Calculate progress and accuracy metrics
  const progressStats = useMemo(() => {
    const total = confidenceDistribution
      ? confidenceDistribution.none +
        confidenceDistribution.low +
        confidenceDistribution.medium +
        confidenceDistribution.high
      : 0

    const aiLabeledImages = confidenceDistribution
      ? confidenceDistribution.low + confidenceDistribution.medium + confidenceDistribution.high
      : 0

    const needsReview = total // All images still in tabs need review

    // Use the actual reviewed images count (images with accepted predictions)
    // This is the TRUE count of human-reviewed images from the API
    const humanReviewed = reviewedImages?.length || 0

    // Calculate the total dataset size (reviewed + still need review)
    const totalDatasetSize = humanReviewed + needsReview

    // Calculate human review goal (10% of total dataset, minimum 1)
    const reviewGoal = Math.max(1, Math.ceil(totalDatasetSize * 0.1))
    const reviewProgress = reviewGoal > 0 ? (humanReviewed / reviewGoal) * 100 : 0

    return {
      total,
      aiLabeledImages,
      needsReview,
      humanReviewed,
      reviewGoal,
      reviewProgress: Math.min(100, reviewProgress), // Cap at 100%
      highQuality: confidenceDistribution?.high || 0,
      totalDatasetSize, // Add this for reference
    }
  }, [confidenceDistribution, reviewedImages, originalUnlabeledCount, completedCount])

  return (
    <>
      <ProgressCard>
        <CardHeader onClick={() => setShowDetailsModal(true)}>
          <CardTitle>
            <FaBullseye />
            Review Progress
          </CardTitle>
        </CardHeader>

        <Section>
          <ProgressBarContainer>
            <ProgressBar>
              <ProgressFill
                $percentage={progressStats.reviewProgress}
                $color={
                  progressStats.reviewProgress >= 100
                    ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
                    : undefined
                }
              />
            </ProgressBar>
            <ProgressText>
              <span>
                {progressStats.humanReviewed} / {progressStats.reviewGoal}
              </span>
              <span>{Math.round(progressStats.reviewProgress)}%</span>
            </ProgressText>
          </ProgressBarContainer>
          {progressStats.reviewProgress >= 100 && (
            <TimeDisplay style={{ color: '#10b981', fontSize: '0.75rem' }}>
              <FaCheckCircle style={{ marginRight: '0.3rem' }} />
              Ready to Fine-Tune!
            </TimeDisplay>
          )}
        </Section>

        <Section>
          <SectionTitle>
            <FaChartBar />
            Dataset Stats
          </SectionTitle>
          <StatsGrid>
            <StatItem>
              <StatValue>{originalUnlabeledCount}</StatValue>
              <StatLabel>Total</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{progressStats.aiLabeledImages}</StatValue>
              <StatLabel>AI Labeled</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{progressStats.needsReview}</StatValue>
              <StatLabel>To Review</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{progressStats.humanReviewed}</StatValue>
              <StatLabel>Reviewed</StatLabel>
            </StatItem>
          </StatsGrid>
        </Section>

        <Section>
          <SectionTitle>
            <FaBrain />
            By Confidence
          </SectionTitle>
          <ConfidenceBreakdown>
            <ConfidenceItem $color="#dc3545">
              <ConfidenceName>None</ConfidenceName>
              <ConfidenceCount>{confidenceDistribution?.none || 0}</ConfidenceCount>
            </ConfidenceItem>
            <ConfidenceItem $color="#fd7e14">
              <ConfidenceName>Low</ConfidenceName>
              <ConfidenceCount>{confidenceDistribution?.low || 0}</ConfidenceCount>
            </ConfidenceItem>
            <ConfidenceItem $color="#ffc107">
              <ConfidenceName>Medium</ConfidenceName>
              <ConfidenceCount>{confidenceDistribution?.medium || 0}</ConfidenceCount>
            </ConfidenceItem>
            <ConfidenceItem $color="#198754">
              <ConfidenceName>High</ConfidenceName>
              <ConfidenceCount>{confidenceDistribution?.high || 0}</ConfidenceCount>
            </ConfidenceItem>
          </ConfidenceBreakdown>
        </Section>

        <InfoBox $type="tip">
          <FaLightbulb style={{ color: '#10b981', flexShrink: 0, marginTop: '0.1rem' }} />
          <InfoText>
            Focus on <strong>None</strong> and <strong>Low</strong> first for maximum impact!
          </InfoText>
        </InfoBox>

        <InfoBox $type="info">
          <FaHandPointer style={{ color: '#9333ea', flexShrink: 0, marginTop: '0.1rem' }} />
          <InfoText>Click any image to review and correct predictions.</InfoText>
        </InfoBox>
      </ProgressCard>

      {/* Review Details Modal */}
      <Modal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        size="xl"
        centered
        style={{ maxWidth: '95vw' }}
      >
        <Modal.Header
          closeButton
          style={{ backgroundColor: '#2a2a2a', borderColor: 'rgba(147, 51, 234, 0.2)' }}
        >
          <Modal.Title
            style={{ color: '#ffffff', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <FaEye style={{ color: '#9333ea' }} />
            Review Progress Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            backgroundColor: '#1f1f1f',
            color: '#ffffff',
            padding: 0,
            height: '80vh',
            minHeight: '600px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Tab.Container defaultActiveKey="images">
            <Nav
              variant="tabs"
              style={{
                backgroundColor: '#2a2a2a',
                borderBottom: '1px solid rgba(147, 51, 234, 0.2)',
              }}
            >
              <Nav.Item>
                <Nav.Link
                  eventKey="images"
                  style={{
                    color: '#b0b0b0',
                    border: 'none',
                    backgroundColor: 'transparent',
                    padding: '1rem 1.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                  className="custom-tab-link"
                >
                  <FaImages />
                  Reviewed Images ({reviewedImages.length})
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  eventKey="summary"
                  style={{
                    color: '#b0b0b0',
                    border: 'none',
                    backgroundColor: 'transparent',
                    padding: '1rem 1.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                  className="custom-tab-link"
                >
                  <FaChartBar />
                  Summary
                </Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content style={{ padding: '1.5rem', flex: 1, overflowY: 'auto', minHeight: 0 }}>
              <Tab.Pane eventKey="images">
                {selectedImageDetail ? (
                  /* Detailed Image View */
                  <div>
                    {/* Back button */}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setSelectedImageDetail(null)}
                      style={{ marginBottom: '1rem' }}
                    >
                      ← Back to Grid
                    </Button>

                    {/* Detailed image with annotations and legend */}
                    <div
                      style={{
                        display: 'flex',
                        gap: '1rem',
                        background: '#1a1a1a',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        padding: '1rem',
                        alignItems: 'stretch',
                      }}
                    >
                      {isDetailedImageLoading ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '400px',
                            width: '100%',
                          }}
                        >
                          <Spinner animation="border" variant="success" />
                        </div>
                      ) : detailedImageUrl ? (
                        <>
                          {/* Image with bounding boxes */}
                          <div
                            style={{
                              flex: 1,
                              position: 'relative',
                              minWidth: 0,
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <img
                              src={detailedImageUrl}
                              alt="Reviewed"
                              style={{
                                width: '100%',
                                height: 'auto',
                                display: 'block',
                                borderRadius: '4px',
                              }}
                            />
                            {/* Only render SVG overlay for bounding box projects, not classification */}
                            {project?.annotation_type !== 'classification' &&
                              selectedImageDetail.bboxes.length > 0 && (
                                <svg
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    pointerEvents: 'none',
                                  }}
                                  viewBox={`0 0 ${selectedImageDetail.width} ${selectedImageDetail.height}`}
                                  preserveAspectRatio="xMidYMid meet"
                                >
                                  {selectedImageDetail.bboxes.map((bbox: any, index: number) => {
                                    // Get tag color
                                    const tag = tags?.tags?.find((t: any) => t.id === bbox.name)
                                    const color = tag?.color
                                      ? `#${tag.color.toString(16).padStart(6, '0')}`
                                      : '#10b981'

                                    // Handle oriented bounding box (polygon)
                                    if (bbox.points && bbox.points.length === 8) {
                                      const points = bbox.points
                                      const pathData = `M ${points[0]} ${points[1]} L ${points[2]} ${points[3]} L ${points[4]} ${points[5]} L ${points[6]} ${points[7]} Z`

                                      return (
                                        <path
                                          key={index}
                                          d={pathData}
                                          fill={`${color}25`}
                                          stroke={color}
                                          strokeWidth="3"
                                          vectorEffect="non-scaling-stroke"
                                        />
                                      )
                                    }

                                    // Handle standard bounding box (rectangle)
                                    if (bbox.xmin !== undefined && bbox.xmax !== undefined) {
                                      const x = bbox.xmin
                                      const y = bbox.ymin
                                      const boxWidth = bbox.xmax - bbox.xmin
                                      const boxHeight = bbox.ymax - bbox.ymin

                                      if (boxWidth <= 0 || boxHeight <= 0) {
                                        return null
                                      }

                                      return (
                                        <rect
                                          key={index}
                                          x={x}
                                          y={y}
                                          width={boxWidth}
                                          height={boxHeight}
                                          fill={`${color}25`}
                                          stroke={color}
                                          strokeWidth="3"
                                          vectorEffect="non-scaling-stroke"
                                        />
                                      )
                                    }

                                    return null
                                  })}
                                </svg>
                              )}
                          </div>

                          {/* Legend with tag names and confidence */}
                          <div
                            style={{
                              width: '250px',
                              background: 'rgba(0, 0, 0, 0.6)',
                              borderRadius: '8px',
                              padding: '1rem',
                              overflowY: 'auto',
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <h6
                              style={{
                                color: '#10b981',
                                marginBottom: '1rem',
                                fontSize: '1rem',
                                fontWeight: '600',
                              }}
                            >
                              {project?.annotation_type === 'classification'
                                ? `Predicted Classes (${selectedImageDetail.bboxes.length})`
                                : `Detected Objects (${selectedImageDetail.bboxes.length})`}
                            </h6>
                            <div
                              style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
                            >
                              {selectedImageDetail.bboxes.map((bbox: any, index: number) => {
                                const tag = tags?.tags?.find((t: any) => t.id === bbox.name)
                                const color = tag?.color
                                  ? `#${tag.color.toString(16).padStart(6, '0')}`
                                  : '#10b981'
                                const tagName = getTagName(bbox.name)

                                return (
                                  <div
                                    key={index}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: '0.5rem',
                                      padding: '0.5rem',
                                      background: 'rgba(255, 255, 255, 0.05)',
                                      borderRadius: '4px',
                                      borderLeft: `3px solid ${color}`,
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: '16px',
                                        height: '16px',
                                        background: color,
                                        borderRadius: '3px',
                                        flexShrink: 0,
                                        marginTop: '2px',
                                      }}
                                    />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div
                                        style={{
                                          color: '#ffffff',
                                          fontSize: '0.9rem',
                                          fontWeight: '600',
                                          marginBottom: '0.25rem',
                                        }}
                                      >
                                        {tagName}
                                      </div>
                                      {bbox.confidence !== undefined &&
                                        bbox.confidence !== null && (
                                          <div style={{ color: '#b0b0b0', fontSize: '0.8rem' }}>
                                            Confidence: {(bbox.confidence * 100).toFixed(1)}%
                                          </div>
                                        )}
                                      {project?.annotation_type === 'classification' &&
                                        (bbox.confidence === undefined ||
                                          bbox.confidence === null) && (
                                          <div style={{ color: '#b0b0b0', fontSize: '0.8rem' }}>
                                            Accepted
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                ) : reviewedImages.length > 0 ? (
                  /* Grid View */
                  <div>
                    <div
                      style={{
                        marginBottom: '1rem',
                        padding: '1rem',
                        background:
                          'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
                        borderRadius: '8px',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div
                          style={{
                            color: '#10b981',
                            fontSize: '1.1rem',
                            fontWeight: '700',
                            marginBottom: '0.25rem',
                          }}
                        >
                          {reviewedImages.length} {reviewedImages.length === 1 ? 'Image' : 'Images'}{' '}
                          Added to Training
                        </div>
                        <div
                          style={{
                            color: '#b0b0b0',
                            fontSize: '0.7rem',
                            lineHeight: '1.3',
                          }}
                        >
                          With accepted predictions • Ready for fine-tuning
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => setImagesPerRow(6)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '36px',
                            height: '36px',
                            background:
                              imagesPerRow === 6
                                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                : 'rgba(42, 42, 42, 0.6)',
                            border: `1px solid ${
                              imagesPerRow === 6
                                ? 'rgba(16, 185, 129, 0.5)'
                                : 'rgba(255, 255, 255, 0.1)'
                            }`,
                            borderRadius: '8px',
                            color: imagesPerRow === 6 ? '#ffffff' : '#b0b0b0',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                          title="Small (6 per row)"
                        >
                          <Grid3x3 size={18} />
                        </button>
                        <button
                          onClick={() => setImagesPerRow(3)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '36px',
                            height: '36px',
                            background:
                              imagesPerRow === 3
                                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                : 'rgba(42, 42, 42, 0.6)',
                            border: `1px solid ${
                              imagesPerRow === 3
                                ? 'rgba(16, 185, 129, 0.5)'
                                : 'rgba(255, 255, 255, 0.1)'
                            }`,
                            borderRadius: '8px',
                            color: imagesPerRow === 3 ? '#ffffff' : '#b0b0b0',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                          title="Medium (3 per row)"
                        >
                          <Grid2x2 size={18} />
                        </button>
                        <button
                          onClick={() => setImagesPerRow(2)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '36px',
                            height: '36px',
                            background:
                              imagesPerRow === 2
                                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                : 'rgba(42, 42, 42, 0.6)',
                            border: `1px solid ${
                              imagesPerRow === 2
                                ? 'rgba(16, 185, 129, 0.5)'
                                : 'rgba(255, 255, 255, 0.1)'
                            }`,
                            borderRadius: '8px',
                            color: imagesPerRow === 2 ? '#ffffff' : '#b0b0b0',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                          title="Large (2 per row)"
                        >
                          <LayoutGrid size={18} />
                        </button>
                      </div>
                    </div>
                    <div style={{ marginTop: '1rem' }}>
                      <GalleryGrid imagesPerRow={imagesPerRow}>
                        {reviewedImages.map((image) => (
                          <GalleryTile
                            key={image.contentid}
                            onClick={() => {
                              setSelectedImageDetail({
                                contentId: image.contentid,
                                projectId: image.projectId,
                                datasetId: image.datasetId,
                                width: image.width,
                                height: image.height,
                                bboxes: image.bboxes,
                              })
                            }}
                          >
                            <ReviewedImageWithBBoxes
                              contentId={image.contentid}
                              projectId={image.projectId}
                              datasetId={image.datasetId}
                              width={image.width}
                              height={image.height}
                              bboxes={image.bboxes}
                              annotation={image.annotation}
                            />
                          </GalleryTile>
                        ))}
                      </GalleryGrid>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      padding: '2rem',
                      textAlign: 'center',
                      background:
                        'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(147, 51, 234, 0.05) 100%)',
                      borderRadius: '8px',
                      border: '1px solid rgba(147, 51, 234, 0.3)',
                    }}
                  >
                    <FaEye
                      style={{
                        color: '#9333ea',
                        fontSize: '3rem',
                        marginBottom: '1rem',
                        opacity: 0.8,
                      }}
                    />
                    <div
                      style={{
                        color: '#c084fc',
                        fontSize: '1.5rem',
                        fontWeight: '800',
                        marginBottom: '0.5rem',
                      }}
                    >
                      {progressStats.humanReviewed}
                    </div>
                    <div
                      style={{
                        color: '#e0e0e0',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        marginBottom: '0.75rem',
                      }}
                    >
                      Images Reviewed
                    </div>
                    <div
                      style={{
                        color: '#b0b0b0',
                        fontSize: '0.75rem',
                        lineHeight: '1.4',
                        maxWidth: '320px',
                        margin: '0 auto',
                      }}
                    >
                      {progressStats.humanReviewed > 0
                        ? "You've reviewed these images, but no predictions were accepted. Images with accepted predictions will appear here and be added to your training dataset."
                        : 'Images with accepted predictions will appear here once you review them.'}
                    </div>
                  </div>
                )}
              </Tab.Pane>

              <Tab.Pane eventKey="summary">
                <div style={{ marginBottom: '1.5rem' }}>
                  <h5
                    style={{
                      color: '#c084fc',
                      fontSize: '0.9rem',
                      marginBottom: '1rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Progress Summary
                  </h5>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div
                      style={{
                        background: 'rgba(147, 51, 234, 0.1)',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid rgba(147, 51, 234, 0.2)',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '2rem',
                          fontWeight: '800',
                          color: '#9333ea',
                          marginBottom: '0.25rem',
                        }}
                      >
                        {progressStats.humanReviewed}
                      </div>
                      <div
                        style={{
                          fontSize: '0.75rem',
                          color: '#b0b0b0',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Images Reviewed
                      </div>
                    </div>
                    <div
                      style={{
                        background: 'rgba(147, 51, 234, 0.1)',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid rgba(147, 51, 234, 0.2)',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '2rem',
                          fontWeight: '800',
                          color: '#9333ea',
                          marginBottom: '0.25rem',
                        }}
                      >
                        {Math.round(progressStats.reviewProgress)}%
                      </div>
                      <div
                        style={{
                          fontSize: '0.75rem',
                          color: '#b0b0b0',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Goal Progress
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <h5
                    style={{
                      color: '#c084fc',
                      fontSize: '0.9rem',
                      marginBottom: '1rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Dataset Breakdown
                  </h5>
                  <div
                    style={{
                      background: 'rgba(60, 60, 60, 0.3)',
                      padding: '1rem',
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ color: '#b0b0b0', fontSize: '0.85rem' }}>
                        Original Images:
                      </span>
                      <span style={{ color: '#ffffff', fontSize: '0.9rem', fontWeight: '700' }}>
                        {originalUnlabeledCount}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ color: '#b0b0b0', fontSize: '0.85rem' }}>AI Labeled:</span>
                      <span style={{ color: '#ffffff', fontSize: '0.9rem', fontWeight: '700' }}>
                        {progressStats.aiLabeledImages}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ color: '#b0b0b0', fontSize: '0.85rem' }}>
                        Still Needs Review:
                      </span>
                      <span style={{ color: '#ffc107', fontSize: '0.9rem', fontWeight: '700' }}>
                        {progressStats.needsReview}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingTop: '0.75rem',
                        borderTop: '1px solid rgba(147, 51, 234, 0.2)',
                      }}
                    >
                      <span style={{ color: '#10b981', fontSize: '0.85rem', fontWeight: '600' }}>
                        Reviewed by You:
                      </span>
                      <span style={{ color: '#10b981', fontSize: '1rem', fontWeight: '800' }}>
                        {progressStats.humanReviewed}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h5
                    style={{
                      color: '#c084fc',
                      fontSize: '0.9rem',
                      marginBottom: '1rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Confidence Distribution
                  </h5>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    {[
                      { label: 'None', count: confidenceDistribution?.none || 0, color: '#dc3545' },
                      { label: 'Low', count: confidenceDistribution?.low || 0, color: '#fd7e14' },
                      {
                        label: 'Medium',
                        count: confidenceDistribution?.medium || 0,
                        color: '#ffc107',
                      },
                      { label: 'High', count: confidenceDistribution?.high || 0, color: '#198754' },
                    ].map((item) => (
                      <div
                        key={item.label}
                        style={{
                          background: 'rgba(60, 60, 60, 0.4)',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          borderLeft: `4px solid ${item.color}`,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <span style={{ color: '#e0e0e0', fontSize: '0.85rem' }}>{item.label}</span>
                        <span
                          style={{
                            color: '#ffffff',
                            fontSize: '0.9rem',
                            fontWeight: '700',
                            background: 'rgba(147, 51, 234, 0.2)',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                          }}
                        >
                          {item.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {progressStats.reviewProgress >= 100 && (
                  <div
                    style={{
                      marginTop: '1.5rem',
                      padding: '1rem',
                      background:
                        'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      borderRadius: '8px',
                      textAlign: 'center',
                    }}
                  >
                    <FaCheckCircle
                      style={{ color: '#10b981', fontSize: '2rem', marginBottom: '0.5rem' }}
                    />
                    <div style={{ color: '#10b981', fontSize: '1rem', fontWeight: '700' }}>
                      Goal Achieved! Ready for Fine-Tuning
                    </div>
                    <div style={{ color: '#b0b0b0', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                      You've reviewed {progressStats.reviewGoal} images (10% of the dataset)
                    </div>
                  </div>
                )}
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>

          <style>{`
            .custom-tab-link.active {
              color: #9333ea !important;
              border-bottom: 2px solid #9333ea !important;
            }
            .custom-tab-link:hover {
              color: #c084fc !important;
            }
          `}</style>
        </Modal.Body>
        <Modal.Footer
          style={{ backgroundColor: '#2a2a2a', borderColor: 'rgba(147, 51, 234, 0.2)' }}
        >
          <Button variant="outline-secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ReviewProgressCard
